'use client';

import { SlotSubscriber } from '@velocity-exchange/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import { slotQueryKeys } from 'src/hooks/query/slot/slotQueryKeys';
import { SlotState } from 'src/hooks/query/slot/useSlotQuery';
import { setForceResubscribeRef } from 'src/hooks/useForceSlotResubscribe';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { slotSubject } from 'src/utils/slotMetrics';

export function useSlotSubscription() {
	const queryClient = useQueryClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const connection = useVelocityStore((s) => s.connection.current);
	const subscriberRef = useRef<SlotSubscriber | null>(null);

	const forceResubscribe = useCallback(async () => {
		const subscriber = subscriberRef.current;
		if (!subscriber) {
			return;
		}

		try {
			await subscriber.unsubscribe(true);
			await subscriber.subscribe();

			const slot = subscriber.getSlot();
			queryClient.setQueryData<SlotState | null>(slotQueryKeys.slot(), {
				currentSlot: slot,
				commitment: connection?.commitment,
				isSubscribed: true,
			});
			slotSubject.next(slot);
		} catch (error) {
			console.error('Failed to resubscribe to slot updates:', error);

			queryClient.setQueryData<SlotState | null>(slotQueryKeys.slot(), (old) =>
				old ? { ...old, isSubscribed: false } : null
			);
		}
	}, [connection?.commitment, queryClient]);

	useEffect(() => {
		setForceResubscribeRef(forceResubscribe);
		return () => {
			setForceResubscribeRef(null);
		};
	}, [forceResubscribe]);

	useEffect(() => {
		if (!velocityClientIsReady || !connection) {
			queryClient.setQueryData<SlotState | null>(
				slotQueryKeys.slot(),
				(old) => {
					if (!old) {
						return null;
					}

					return {
						...old,
						isSubscribed: false,
					};
				}
			);
			return;
		}

		const subscriber = new SlotSubscriber(connection, { resubTimeoutMs: 2000 });
		subscriberRef.current = subscriber;

		let isCleanedUp = false;

		const syncInitialSlot = async () => {
			try {
				await subscriber.subscribe();

				if (isCleanedUp) {
					return;
				}

				const slot = subscriber.getSlot();

				queryClient.setQueryData<SlotState | null>(slotQueryKeys.slot(), {
					currentSlot: slot,
					commitment: connection.commitment,
					isSubscribed: true,
				});
				slotSubject.next(slot);
			} catch (error) {
				console.error('Failed to subscribe to slot updates:', error);

				if (isCleanedUp) {
					return;
				}

				queryClient.setQueryData<SlotState | null>(
					slotQueryKeys.slot(),
					(old) => {
						if (!old) {
							return {
								currentSlot: 0,
								commitment: connection.commitment,
								isSubscribed: false,
							};
						}

						return {
							...old,
							isSubscribed: false,
						};
					}
				);
			}
		};

		const handleNewSlot = (slot: number) => {
			queryClient.setQueryData<SlotState | null>(
				slotQueryKeys.slot(),
				(old) => ({
					currentSlot: slot,
					commitment: old?.commitment ?? connection.commitment,
					isSubscribed: true,
				})
			);
			slotSubject.next(slot);
		};

		syncInitialSlot();
		subscriber.eventEmitter.on('newSlot', handleNewSlot);

		return () => {
			isCleanedUp = true;

			subscriber.eventEmitter.removeListener('newSlot', handleNewSlot);

			subscriber.unsubscribe().catch((error) => {
				console.error('Failed to unsubscribe from slot updates:', error);
			});

			subscriberRef.current = null;

			queryClient.setQueryData<SlotState | null>(
				slotQueryKeys.slot(),
				(old) => {
					if (!old) {
						return null;
					}

					return {
						...old,
						isSubscribed: false,
					};
				}
			);
		};
	}, [connection, velocityClientIsReady, queryClient]);

	return { forceResubscribe };
}
